import { LoadingState } from "@react-types/shared";
import React, { RefObject, useRef } from "react";
import { AriaListBoxOptions, useListBox, useOption } from "react-aria";
import { ListState, Node } from "react-stately";
import * as S from "./styled";
import { Spinner } from "./Spinner";
import Flex from "../../Flex";

type Props<T> = AriaListBoxOptions<T> & {
    listBoxRef?: RefObject<HTMLUListElement>;
    state: ListState<unknown>;
    loadingState?: LoadingState;
    onLoadMore?: () => void;
};

export const ListBox = <T extends unknown>(props: Props<T>) => {
    const ref = useRef(null);
    const { listBoxRef = ref, state } = props;
    const { listBoxProps } = useListBox(props, state, listBoxRef);

    const handleScroll = (e: React.UIEvent) => {
        const scrollOffset = e.currentTarget.scrollHeight - e.currentTarget.clientHeight * 2;

        if (e.currentTarget.scrollTop > scrollOffset && props.onLoadMore) {
            props.onLoadMore();
        }
    };

    return (
        <S.List {...listBoxProps} as="ul" column ref={listBoxRef} onScroll={handleScroll}>
            {[...state.collection].map(item => (
                <Option key={item.key} item={item} state={state} />
            ))}
            {props.loadingState === "loadingMore" && (
                <Flex column py={4} role="option">
                    <Spinner />
                </Flex>
            )}
        </S.List>
    );
};

type OptionProps = {
    item: Node<unknown>;
    state: ListState<unknown>;
};

const Option = ({ item, state }: OptionProps) => {
    const ref = useRef(null);
    const { optionProps, isSelected, isFocused, isDisabled } = useOption({ key: item.key }, state, ref);

    return (
        <S.ListItem
            {...optionProps}
            as="li"
            $isDisabled={isDisabled}
            $isFocused={isFocused}
            $isSelected={isSelected}
            ref={ref}
        >
            {item.rendered}
        </S.ListItem>
    );
};
